<template>
  <div class="p-6 md:p-10 bg-white min-h-screen font-sans text-slate-800 select-none">
    
    <!-- Header / Titre -->
    <header class="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 border-b border-slate-100 pb-6">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <span class="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-black uppercase rounded-full tracking-wider border border-blue-100">
            {{ t.title }}
          </span>
        </div>
        <h1 class="text-3xl font-black text-slate-900 tracking-tight">Gestion des Périphériques</h1>
        <p class="text-xs text-slate-500 font-medium mt-1">{{ t.subtitle }}</p>
      </div>

     
    </header>

    <main class="max-w-7xl mx-auto space-y-6">

      <!-- Barre de Recherche -->
      <div class="bg-slate-50/80 p-4 rounded-2xl border border-slate-200/80 flex flex-wrap gap-4 items-center shadow-sm">
        <div class="flex-1 min-w-[280px] relative">
          <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </span>
          <input type="text" v-model="search" :placeholder="t.searchPlaceholder" class="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:border-blue-600 transition-all text-xs font-semibold text-slate-800 outline-none shadow-sm">
        </div>
      </div>

      <!-- Table Kiosks / Périphériques -->
      <div class="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <template v-if="filteredKiosks.length === 0">
          <div class="p-16 text-center text-slate-400 font-bold uppercase tracking-wider text-xs">
            🚀 {{ t.noKiosk }}
          </div>
        </template>

        <template v-else>
          <div class="overflow-x-auto">
            <table class="w-full border-collapse text-left">
              <thead>
                <tr class="bg-slate-50 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-200/80">
                  <th class="px-6 py-4">{{ t.deviceType }}</th>
                  <th class="px-6 py-4">{{ t.name }}</th>
                  <th class="px-6 py-4 text-center">{{ t.status }}</th>
                  <th class="px-6 py-4">{{ t.lastUpdate }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100 text-xs">
                <tr v-for="kiosk in filteredKiosks" :key="kiosk.id" class="hover:bg-blue-50/40 transition-colors group cursor-pointer" @click="goToLayout(kiosk)">
                  <td class="px-6 py-4 font-bold text-slate-900">{{ kiosk.type }}</td> 
                  <td class="px-6 py-4 font-semibold text-slate-700">{{ kiosk.name }}</td>
                  <td class="px-6 py-4 text-center">
                    <span :class="statusClasses(kiosk.status)" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider border">
                      <span class="w-1.5 h-1.5 rounded-full" :class="kiosk.status === 'active' ? 'bg-emerald-500' : kiosk.status === 'maintenance' ? 'bg-amber-500' : 'bg-rose-500'"></span>
                      {{ kiosk.statusLabel }}
                    </span>
                  </td>
                  <td class="px-6 py-4 text-slate-500 font-medium">{{ kiosk.lastUpdate }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>

    </main>



  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
export default {
  data() {
    return {
      kiosks: [],
      search: '',
      showAddModal: false,
      newKiosk: { id: '', status: 'active' },
      currentLanguage: localStorage.getItem("appLanguage") || "fr",
      translations: {
        fr: {
          title: "Périphérique de l'agence",
          subtitle: "Suivi des kiosks et navigation vers l'édition du layout.",
          searchPlaceholder: "Rechercher un kiosk par ID ou statut...",
          noKiosk: "Aucun kiosk trouvé",
          deviceType: "Type de Périphérique",
          name: "Nom",
          status: "Statut",
          lastUpdate: "Dernière MAJ",
          online: "Online",
          offline: "Offline",
          addKiosk: "Ajouter un Kiosk",
          addKioskSubtitle: "Configurez le kiosk pour l'agence.",
          kioskId: "ID du kiosk",
          active: "Actif",
          maintenance: "Maintenance",
          suspended: "Suspendu",
          cancel: "Annuler",
          createKiosk: "Créer Kiosk"
        },
        en: {
          title: "Agency Devices",
          subtitle: "Kiosk monitoring and layout editing navigation.",
          searchPlaceholder: "Search kiosk by ID or status...",
          noKiosk: "No kiosk found",
          deviceType: "Device Type",
          name: "Name",
          status: "Status",
          lastUpdate: "Last Update",
          online: "Online",
          offline: "Offline",
          addKiosk: "Add Kiosk",
          addKioskSubtitle: "Configure the kiosk for the agency.",
          kioskId: "Kiosk ID",
          active: "Active",
          maintenance: "Maintenance",
          suspended: "Suspended",
          cancel: "Cancel",
          createKiosk: "Create Kiosk"
        },
        de: {
          title: "Agenturgeräte",
          subtitle: "Kiosk-Überwachung und Layout-Bearbeitungsnavigation.",
          searchPlaceholder: "Kiosk nach ID oder Status suchen...",
          noKiosk: "Kein Kiosk gefunden",
          deviceType: "Gerätetyp",
          name: "Name",
          status: "Status",
          lastUpdate: "Letzte Aktualisierung",
          online: "Online",
          offline: "Offline",
          addKiosk: "Kiosk hinzufügen",
          addKioskSubtitle: "Konfigurieren Sie den Kiosk für die Agentur.",
          kioskId: "Kiosk-ID",
          active: "Aktiv",
          maintenance: "Wartung",
          suspended: "Ausgesetzt",
          cancel: "Abbrechen",
          createKiosk: "Kiosk erstellen"
        },
        it: {
          title: "Dispositivi Agenzia",
          subtitle: "Monitoraggio dei chioschi e navigazione modifica layout.",
          searchPlaceholder: "Cerca chiosco per ID o stato...",
          noKiosk: "Nessun chiosco trovato",
          deviceType: "Tipo di Dispositivo",
          name: "Nome",
          status: "Stato",
          lastUpdate: "Ultimo Aggiornamento",
          online: "Online",
          offline: "Offline",
          addKiosk: "Aggiungi Chiosco",
          addKioskSubtitle: "Configura il chiosco per l'agenzia.",
          kioskId: "ID Chiosco",
          active: "Attivo",
          maintenance: "Manutenzione",
          suspended: "Sospeso",
          cancel: "Annulla",
          createKiosk: "Crea Chiosco"
        }
      }
    };
  },
  computed: {
    t() {
      return this.translations[this.currentLanguage] || this.translations.fr;
    },
    filteredKiosks() {
      if (!this.search) return this.kiosks;
      return this.kiosks.filter(k => 
        (k.id && k.id.toLowerCase().includes(this.search.toLowerCase())) || 
        (k.statusLabel && k.statusLabel.toLowerCase().includes(this.search.toLowerCase())) ||
        (k.name && k.name.toLowerCase().includes(this.search.toLowerCase()))
      );
    }
  },
  mounted() {
    this.fetchKiosks();
  },
  methods: {
    async fetchKiosks() {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("userToken");

        const res = await fetch("https://api.datafuse.fr/api/agency/kiosks", {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || "Erreur récupération kiosks");
        }

        const data = await res.json();
        this.kiosks = data.map(k => ({
          id: k.id || k._id,
          name: k.name || 'Borne Principale',
          type: k.type || 'Standard',
          identifier: k.identifier,
          agencyName: k.agencyName,
          agencyId: k.agencyId,
          totalKiosks: k.totalKiosks,
          online: k.online,
          status: k.online ? "active" : "inactive",
          statusLabel: k.online ? "Online" : "Offline",
          lastUpdate: k.lastUpdate ? new Date(k.lastUpdate).toLocaleDateString() : 'N/A'
        }));

      } catch (err) {
        console.error(err);
        toast.error("Impossible de récupérer les kiosks");
      }
    },
    statusClasses(status) {
      switch(status) {
        case 'active': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
        case 'maintenance': return 'bg-amber-50 text-amber-700 border-amber-200';
        default: return 'bg-rose-50 text-rose-700 border-rose-200';
      }
    },
    goToLayout(kiosk) {
      this.$router.push({
        name: 'KioskLayout',
        params: { kioskId: kiosk.agencyId || kiosk.id }
      });
    },
    saveNewKiosk() {
      const kiosk = {
        id: this.newKiosk.id,
        name: 'Nouveau Kiosk',
        type: 'Standard',
        status: this.newKiosk.status,
        statusLabel: this.newKiosk.status === 'active' ? 'Online' : this.newKiosk.status === 'maintenance' ? 'Maintenance' : 'Offline',
        lastUpdate: new Date().toLocaleDateString()
      };
      this.kiosks.push(kiosk);
      toast.success("Kiosk créé avec succès !");
      this.showAddModal = false;
      this.newKiosk = { id: '', status: 'active' };
    }
  }
}
</script>